import chroma from "chroma-js";
import {RelevanceLevel} from "./routines/schemas";

// ─── Brand palette ───────────────────────────────────────────────────────────
// Cohesive muted-violet scale. 100–200 are surface tints, 500–700 are the
// main brand hues, 800–900 are inks. Slightly desaturated from Tailwind's
// `violet-*` so the brand reads as grown-up rather than candy.
//
// Listed as literal hex codes (and also as full Tailwind classes below) so
// the JIT scanner picks them up — arbitrary values built from template
// literals would not be extracted.
export const BrandHex = {
    50:  "#f7f5fc",
    100: "#ede9f7",
    200: "#dcd2ed",
    300: "#bcabe0",
    400: "#9c83cf",
    500: "#7e63bf",
    600: "#6a4eaa",
    700: "#564191",
    800: "#42316f",
    900: "#291f48",
} as const;

// ─── Surface palette (warm-neutral) ──────────────────────────────────────────
// A slightly warmer near-zinc that harmonizes with the violet brand.
export const SurfaceHex = {
    50:  "#fafafb",
    100: "#f4f4f6",
    200: "#e8e8ec",
    300: "#d1d1d8",
    400: "#9999a3",
    500: "#6e6e7a",
    600: "#4d4d57",
    700: "#34343c",
    800: "#22222a",
    900: "#15151b",
} as const;

// ─── Semantic tokens (preferred for new code) ───────────────────────────────
// Each token is a string of Tailwind classes ready to be concatenated into a
// className. Prefer these over ad-hoc colors so the whole app moves together.
export const Surface = {
    base:    "bg-white",
    muted:   "bg-[#fafafb]",
    sunken:  "bg-[#f4f4f6]",
    tinted:  "bg-[#f7f5fc]",   // very subtle brand wash
    inverse: "bg-[#15151b]",
    brandSoft: "bg-[#ede9f7]",
};

export const Ink = {
    primary:   "text-[#15151b]",
    secondary: "text-[#4d4d57]",
    muted:     "text-[#6e6e7a]",
    subtle:    "text-[#9999a3]",
    onDark:    "text-zinc-50",
    onBrand:   "text-white",
    brand:     "text-[#564191]",
    brandStrong: "text-[#42316f]",
};

export const Border = {
    DEFAULT: "border border-[#e8e8ec]",
    soft:    "border border-[#f4f4f6]",
    strong:  "border border-[#d1d1d8]",
    brand:   "border border-[#bcabe0]",
    brandSoft: "border border-[#dcd2ed]",
};

// Action tokens for primary buttons / links / pills.
export const Brand = {
    solid:        "bg-[#6a4eaa] text-white",
    solidHover:   "hover:bg-[#564191]",
    soft:         "bg-[#ede9f7] text-[#42316f]",
    softHover:    "hover:bg-[#dcd2ed]",
    outline:      "border border-[#bcabe0] text-[#564191]",
    outlineHover: "hover:bg-[#f7f5fc] hover:border-[#9c83cf]",
    ring:         "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9c83cf] focus-visible:ring-offset-2",
    underline:    "underline decoration-[#bcabe0] underline-offset-4",
};

export const Radii = {
    sm:   "rounded-md",
    md:   "rounded-lg",
    lg:   "rounded-xl",
    xl:   "rounded-2xl",
    pill: "rounded-full",
};

export const Shadows = {
    none:  "",
    sm:    "shadow-sm",
    md:    "shadow-md",
    soft:  "shadow-[0_4px_24px_-12px_rgba(60,40,110,0.18)]",
    lift:  "shadow-[0_12px_32px_-16px_rgba(60,40,110,0.28)]",
    glow:  "shadow-[0_8px_24px_-8px_rgba(106,78,170,0.35)]",
    inset: "shadow-inner",
};

export const Motion = {
    base: "transition duration-200 ease-out",
    fast: "transition duration-150 ease-out",
    slow: "transition duration-300 ease-out",
};

// ─── Legacy tokens (kept for compat — values refreshed) ─────────────────────
// Existing code references these; the keys stay the same but the underlying
// values now point at the new brand/surface palette so older surfaces shift
// in tandem with new code.
export const ThemeColors = {
    white: "bg-white ",

    neutralLight: "bg-[#fafafb] ",
    neutralMid:   "bg-[#e8e8ec] ",
    neutralDark:  "bg-[#15151b] ",
    neutralDarkHex: "#15151b ",

    // Muted "midnight-violet" spectrum
    colorLight:    " bg-[#ede9f7] ",
    colorMid:      " bg-[#9c83cf] ",
    colorMidHex:   " #9c83cf ",
    colorDark:     " bg-[#42316f] ",

    colorContrastLight:    "bg-[#bcabe0] ",
    colorContrastLightHex: "#bcabe0 ",
};

export const ThemeColorsHover = {
    white: "hover:bg-[#fafafb] ",

    neutralLight: "hover:bg-[#f4f4f6] ",
    neutralMid:   "hover:bg-[#d1d1d8] ",
    neutralDark:  "hover:bg-[#22222a] ",

    colorLight: "hover:bg-[#dcd2ed] ",
    hoverMid:   "hover:text-[#564191] ",
    colorDark:  "hover:bg-[#564191] ",
};

export const ThemeColorsBorder = {
    neutralLight: "border-[#f4f4f6] ",
    neutralMid:   "border-[#e8e8ec] ",
    neutralDark:  "border-[#34343c] ",

    colorLight: "border-[#dcd2ed] ",
    colorMid:   "border-[#bcabe0] ",
    colorDark:  "border-[#9c83cf] ",

    error: "border-[#e87d7d] ",
};

export const TextColors = {
    light:      "text-zinc-50 ",
    mid:        "text-[#4d4d57] ",
    hoverMid:   "hover:text-[#22222a] ",
    dark:       "text-[#15151b] ",

    hoverColor: "hover:text-[#564191] ",
    colored:    "text-[#564191] ",
};

export const ButtonColors = {
    light:     "bg-[#fafafb] ",
    hoverLight:"hover:bg-[#f4f4f6] ",

    mid:  " bg-[#dcd2ed] ",
    dark: " bg-[#42316f] ",
    hoverDark: " hover:bg-[#564191] ",
};

// ─── Heatmap tones: light red → soft amber → light blue ─────────────────────
export const POSITIVE_END_COLOR = "#f27878"; // 🔥 lighter red (Hot)
export const MIDDLE_COLOR       = "#f5ce63"; // ☀️ lighter amber (Warm)
export const NEGATIVE_END_COLOR = "#6fb1f0"; // ❄️ lighter blue (Cold)

export const ColorMap: Record<RelevanceLevel, string> = {
    Hot: "red",
    Warm: "amber",
    Cold: "blue",
};

/**
 * Smooth perceptual gradient preserving hue from blue → amber → red.
 */
export const calculateScoreColor = (score: number): string => {
    const clamped = Math.max(-1, Math.min(1, score));
    const normalized = (clamped + 1) / 2; // -1 → 0, +1 → 1

    const scale = chroma
        .scale([NEGATIVE_END_COLOR, MIDDLE_COLOR, POSITIVE_END_COLOR])
        .mode("lrgb")
        .gamma(0.8);

    return scale(normalized).saturate(1.1).brighten(0.6).hex();
};

/**
 * Discrete colors matched visually to the continuous gradient.
 */
export const RelevanceLevelColors = {
    Hot:  chroma(POSITIVE_END_COLOR).saturate(0.6).brighten(0.2).hex(),
    Warm: chroma(MIDDLE_COLOR).set("hsl.h", 40).brighten(0.3).hex(),
    Cold: chroma(NEGATIVE_END_COLOR).brighten(0.3).hex(),
};

/**
 * Confidence / tagging color utilities.
 */
export const TaggingConfidenceColors = {
    fromScore(score: number): string {
        return calculateScoreColor(score);
    },
    proposedTag: chroma(MIDDLE_COLOR).brighten(0.5).alpha(0.6).hex(),
};

// ─── Watch phrase colors: gently pastel for contrast ────────────────────────
export const WatchPhraseHighlightColors = {
    fromId(id?: number): string {
        const hue = id != null ? Math.abs(id) % 360 : Math.floor(Math.random() * 360);
        return chroma.hsl(hue, 0.25, 0.7).hex();
    },
    proposedTag: "#bdbdc7",
};
